import classNames from 'classnames';
import type { HTMLAttributes, ReactNode } from 'react';

import { useUiMessages } from '../../messages';
import { Icon } from '../Icon';

import styles from './Badge.module.css';

export type BadgeSize = 'sm' | 'md' | 'lg';
/**
 * Semantic color scale — maps to the `--{color}-chip-*` roles. `orange` reads the palette's
 * `orange-dark` scale (`--orange-dark-chip-*`), the "Orange dark" the Figma library draws, e.g. a
 * High severity between warning and error (HS-961).
 */
export type BadgeColor =
  | 'gray'
  | 'brand'
  | 'error'
  | 'warning'
  | 'success'
  | 'blue'
  | 'purple'
  | 'cyan'
  | 'yellow'
  | 'orange';
/** `color` = soft filled surface; `modern` = white surface with a neutral border + shadow. */
export type BadgeVariant = 'color' | 'modern';

export interface BadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'color'> {
  size?: BadgeSize;
  color?: BadgeColor;
  variant?: BadgeVariant;
  /** Leading status dot, tinted with the color's `--{color}-500`. */
  dot?: boolean;
  /** Icon before the label — any node (`<Icon>`, an SVG, …). */
  iconLeading?: ReactNode;
  /** Icon after the label. */
  iconTrailing?: ReactNode;
  /**
   * Square icon-only badge: renders `iconLeading` alone. Provide an accessible
   * name via `aria-label`, or a string `children` (used as the label automatically).
   */
  iconOnly?: boolean;
  /** Render a trailing dismiss (×) button. */
  onClose?: () => void;
  /** Accessible name for the dismiss button — pass a localized string. */
  closeLabel?: string;
  children?: ReactNode;
}

/**
 * Compact status/label chip. Soft-filled (`color`) or neutral (`modern`) surface,
 * with optional leading dot/icon, trailing icon, or a dismiss button.
 *
 * @interim Ported from the reference design; replaceable by the UI Kit without API breakage.
 */
export function Badge({
  size = 'md',
  color = 'gray',
  variant = 'color',
  dot = false,
  iconLeading,
  iconTrailing,
  iconOnly = false,
  onClose,
  closeLabel,
  children,
  className,
  'aria-label': ariaLabel,
  ...rest
}: BadgeProps) {
  const messages = useUiMessages();
  const resolvedCloseLabel = closeLabel ?? messages.badge?.remove ?? 'Remove';
  const resolvedLabel =
    ariaLabel ?? (iconOnly && typeof children === 'string' ? children : undefined);

  if (iconOnly) {
    return (
      <span
        aria-label={resolvedLabel}
        role={resolvedLabel ? 'img' : undefined}
        className={classNames(
          styles.badge,
          styles[size],
          styles[variant],
          styles[color],
          styles['icon-only'],
          className,
        )}
        {...rest}
      >
        <span className={styles.icon}>{iconLeading}</span>
      </span>
    );
  }

  return (
    <span
      className={classNames(
        styles.badge,
        styles[size],
        styles[variant],
        styles[color],
        dot && styles['with-dot'],
        Boolean(iconLeading) && styles['has-leading'],
        Boolean(iconTrailing) && styles['has-trailing'],
        Boolean(onClose) && styles['has-close'],
        className,
      )}
      {...rest}
    >
      {dot ? <span className={styles.dot} aria-hidden="true" /> : null}
      {iconLeading ? <span className={styles.icon}>{iconLeading}</span> : null}
      {children != null ? <span className={styles.text}>{children}</span> : null}
      {iconTrailing ? <span className={styles.icon}>{iconTrailing}</span> : null}
      {onClose ? (
        <button
          type="button"
          aria-label={resolvedCloseLabel}
          className={styles.close}
          onClick={onClose}
        >
          <Icon name="x-close" />
        </button>
      ) : null}
    </span>
  );
}
